import { useCallback } from 'react';
import { isNil } from '~/utilities/type-guards';
import type { Plan } from '~/graphql';
import { environment } from '~/constantDefinitions';
import { useAnonymousGetGtmDataQuery, useGetGtmDataQuery } from './__gql/useGTMQuery.gql.gen';

type GTMEventNames =
  | 'w.account.created'
  | 'w.account.purchased'
  | 'w.account.trialStarted'
  | 'w.gatedFeature.promoteUpgradeClick'
  | 'w.liveRegistrationFormSubmission'
  | 'w.pageView';

type GTMEventVars = {
  conversion_value?: string;
  planSku?: Plan['sku'];
  name?: string;
  email?: string;
};

interface UseGTMOptions {
  isAnonymous: boolean;
}

const pushToGTMDataLayer = (data: Record<string, unknown>) => {
  if (window.GTMDataLayer) {
    window.GTMDataLayer.push(data);
    return;
  }

  if (environment !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(`useGTM - Event not sent: \n ${JSON.stringify(data, null, 2)}\n`);
  }
};

const useAnonymousGTM = () => {
  const { data, error } = useAnonymousGetGtmDataQuery();

  if (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }

  return useCallback(
    (eventName: GTMEventNames, otherVars?: GTMEventVars) => {
      if (isNil(data)) {
        // eslint-disable-next-line no-console
        console.warn(`useGTM: ${eventName} could not be sent. GQL data not present.`);
        return;
      }

      pushToGTMDataLayer({
        accountId: data.currentAccount?.numericId,
        ...{ event: eventName, ...(otherVars ?? {}) },
      });
    },
    [data],
  );
};

const useAuthenticatedGTM = () => {
  const { data, error } = useGetGtmDataQuery();

  if (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }

  return useCallback(
    (eventName: GTMEventNames, otherVars?: GTMEventVars) => {
      if (isNil(data) || isNil(data.currentContact) || isNil(data.currentAccount)) {
        // eslint-disable-next-line no-console
        console.warn(`useGTM: ${eventName} could not be sent. GQL data not present.`);
        return;
      }

      const isFreeOrPlusPlan =
        data.currentAccount.type === 'FreebieAccount' ||
        data.currentAccount.plan?.tier === 'plus' ||
        data.currentAccount.plan?.tier === 'starter';
      const sendChurnZeroData =
        (data.currentContact.isSuperadmin || data.currentContact.isOwner) &&
        !data.currentContact.isWadmin;
      const churnZeroAccountId = sendChurnZeroData ? data.currentAccount.churnZeroAccountId : null;
      const currentContactEmail = sendChurnZeroData ? data.currentContact.email : null;

      pushToGTMDataLayer({
        accountId: data.currentAccount.numericId,
        contactId: data.currentContact.numericId,
        userId: data.currentContact.numericUserId,
        churnZeroAccountId,
        churnZeroContactId: currentContactEmail,
        isFreeOrPlusPlan,
        ...{ event: eventName, ...(otherVars ?? {}) },
      });
    },
    [data],
  );
};

export const useGTM = (opts?: UseGTMOptions): ReturnType<typeof hook> => {
  const hook = opts?.isAnonymous ? useAnonymousGTM : useAuthenticatedGTM;
  return hook();
};
